import { useEffect, useRef, useState } from "react";
import { Menu, PanelLeftClose, X, Search, ShieldCheck, Waypoints, BookOpen, Network } from "lucide-react";

const groups = [
  { label: "Lookup", icon: Search, links: [["/dns-lookup", "DNS lookup"], ["/", "Propagation checker"], ["/whois-lookup", "WHOIS lookup"]] },
  { label: "Diagnose", icon: ShieldCheck, links: [["/dns-health", "DNS health"], ["/delegation-check", "Delegation check"], ["/dnssec-check", "DNSSEC check"], ["/cname-chain", "CNAME chain"], ["/spf-generator", "SPF generator"], ["/dmarc-generator", "DMARC generator"]] },
  { label: "Plan & monitor", icon: Waypoints, links: [["/dns-migration-checklist", "Migration checklist"], ["/dns-change-tracker", "Change tracker"], ["/dns-alerts", "Email alerts"]] },
  { label: "Network", icon: Network, links: [["/what-is-my-ip", "My IP address"], ["/subnet-calculator", "Subnet calculator"], ["/ipv6-tools", "IPv6 tools"]] },
  { label: "Learn", icon: BookOpen, links: [["/guide/dns-records", "DNS guide"], ["/glossary", "Glossary"], ["/api", "API"], ["/about", "About WhereIsDNS"]] },
];

function Links({ current }: { current: string }) {
  return <nav aria-label="Tools and resources">{groups.map(({ label, icon: Icon, links }) => (
    <details key={label} open className="tool-nav-group">
      <summary><Icon size={16} aria-hidden="true" /><span>{label}</span></summary>
      <div>{links.map(([href, title]) => <a key={href} href={href} aria-current={current === href ? "page" : undefined}>{title}</a>)}</div>
    </details>
  ))}</nav>;
}

export function ToolNavigation({ current }: { current: string }) {
  const isTool = current !== "/";
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("whereisdns:sidebar-collapsed") === "true"; } catch { return false; }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [desktop, setDesktop] = useState(() => window.matchMedia("(min-width: 1100px)").matches);
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const media = window.matchMedia("(min-width: 1100px)");
    const update = () => { setDesktop(media.matches); dialog.current?.close(); };
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    if (!drawerOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [drawerOpen]);

  function toggle() {
    if (isTool && window.matchMedia("(min-width: 1100px)").matches) {
      setCollapsed(!collapsed);
      try { localStorage.setItem("whereisdns:sidebar-collapsed", String(!collapsed)); } catch { /* Storage is optional. */ }
    } else {
      dialog.current?.showModal();
      setDrawerOpen(true);
    }
  }
  function close() { dialog.current?.close(); }
  return <>
    <button ref={trigger} type="button" className="tool-nav-toggle" onClick={toggle}
      aria-label="Toggle tool navigation" title="Toggle tool navigation"
      aria-expanded={desktop && isTool ? !collapsed : drawerOpen} aria-controls={desktop && isTool ? "tool-sidebar" : "tool-drawer"}>
      {desktop && isTool && !collapsed ? <PanelLeftClose size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
    </button>
    {isTool && <aside id="tool-sidebar" className="tool-sidebar" hidden={collapsed}><Links current={current} /></aside>}
    <dialog ref={dialog} id="tool-drawer" className="tool-drawer" aria-labelledby="tool-drawer-title"
      onClose={() => { setDrawerOpen(false); trigger.current?.focus(); }}
      onClick={(event) => { if (event.target === event.currentTarget) close(); }}>
      <div className="tool-drawer-heading"><strong id="tool-drawer-title">Tools & resources</strong>
        <button type="button" className="tool-nav-toggle" onClick={close} aria-label="Close navigation" title="Close navigation"><X size={20} aria-hidden="true" /></button>
      </div>
      <Links current={current} />
    </dialog>
  </>;
}
